import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { OrangeTextButton } from "../../../../common/OrangeTextButton";
import { useFlexIgnoreZonesOnboardingStore } from "./flexIgnoreZonesOnboardingStore";
import { PresetEditorStep } from "./PresetEditorStep";

export type FlexIgnoreZonesNewPresetDialogCloseMeta = {
  presetLocalId?: string;
};

export const FlexIgnoreZonesNewPresetDialog = ({
  open,
  scheduleId,
  cameraId,
  presetLocalId,
  cameraName,
  assignToSchedule = true,
  showPresetNameField = true,
  onClose,
}: {
  open: boolean;
  scheduleId?: number;
  cameraId: number;
  presetLocalId: string | null;
  cameraName: string;
  assignToSchedule?: boolean;
  /** When true (default), show preset name field and require a non-empty name on submit. */
  showPresetNameField?: boolean;
  onClose: (
    reason: "submitted" | "cancelled",
    meta?: FlexIgnoreZonesNewPresetDialogCloseMeta
  ) => void;
}) => {
  const {
    presetsByCamera,
    cameras,
    cameraContexts,
    loadCameraContext,
    updatePresetName,
    updatePresetZones,
    setSelectedPresetForScheduleCamera,
    removeLocalPresetDraft,
    persistFlexPresetDraftToApi,
  } = useFlexIgnoreZonesOnboardingStore();

  const [saving, setSaving] = React.useState(false);

  const preset =
    presetLocalId != null
      ? (presetsByCamera[cameraId] || []).find((p) => p.localId === presetLocalId)
      : undefined;
  const cameraContext = cameraContexts[cameraId];
  const cameraSummary = cameras.find((camera) => camera.id === cameraId);
  const fallbackPreviewUrl = (cameraSummary?.preview_url || "").trim();
  const resolvedPreviewUrl = (
    cameraContext?.preview?.previewUrl || fallbackPreviewUrl
  ).trim();
  const resolvedCameraContext =
    resolvedPreviewUrl.length > 0
      ? {
          ...cameraContext,
          preview: {
            previewUrl: resolvedPreviewUrl,
            adjustedDimensions: cameraContext?.preview?.adjustedDimensions || {
              x: 720,
              y: 405,
            },
          },
        }
      : cameraContext;

  React.useEffect(() => {
    if (!open || !cameraId) {
      return;
    }
    loadCameraContext(cameraId, true);
  }, [open, cameraId, loadCameraContext]);

  React.useEffect(() => {
    if (!open) {
      setSaving(false);
    }
  }, [open]);

  const handleCancel = () => {
    if (presetLocalId) {
      removeLocalPresetDraft(cameraId, presetLocalId);
    }
    onClose("cancelled");
  };

  const handleSubmit = async () => {
    if (showPresetNameField && !preset?.name?.trim()) {
      alert("Preset name is required.");
      return;
    }
    if (!presetLocalId) {
      return;
    }
    if (preset?.name?.trim()) {
      updatePresetName(cameraId, presetLocalId, preset.name.trim());
    }

    if (assignToSchedule && typeof scheduleId === "number") {
      setSelectedPresetForScheduleCamera(scheduleId, cameraId, presetLocalId);
      onClose("submitted", { presetLocalId });
      return;
    }

    setSaving(true);
    try {
      const result = await persistFlexPresetDraftToApi(cameraId, presetLocalId);
      if (!result.ok) {
        alert(result.error);
        return;
      }
      await loadCameraContext(cameraId, true);
      onClose("submitted", { presetLocalId: result.newLocalId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          handleCancel();
        }
      }}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          fontFamily: "Mulish",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogContent dividers sx={{ overflow: "auto" }}>
        {preset && (
          <PresetEditorStep
            cameraName={cameraName}
            cameraContext={resolvedCameraContext}
            preset={preset}
            onPresetNameChange={(value) =>
              updatePresetName(cameraId, preset.localId, value)
            }
            onPresetZonesChange={(zones) =>
              updatePresetZones(cameraId, preset.localId, zones)
            }
            showPresetMeta={showPresetNameField}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ fontFamily: "Mulish", px: 3, pb: 2 }}>
        <OrangeTextButton
          onClick={handleCancel}
          text="Cancel"
          disabled={saving}
        />
        <OrangeTextButton
          onClick={() => void handleSubmit()}
          text={saving ? "Saving…" : "Submit"}
          disabled={!preset || saving}
        />
      </DialogActions>
    </Dialog>
  );
};
